'use client';
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import CenteredBox from '@/app/base-ui/CenterBox';
import ButtonChooseCustom from '@/app/base-ui/ButtonCustom';
import { Box, Button, CardContent, FormControl, FormLabel, Skeleton, Typography } from '@mui/material';
import BaseTextField from '@/app/base-ui/BaseTextField';
import utilsService from '@/app/helpers/utilsService';
import BaseCardNoticeGray from '@/app/base-ui/BaseCardNoticeGray';
import YouTubeThumbnail from './components/YouTubeThumbnail';
import HelperText from '../../Auth/Registration/components/HelperText';
import { useTranslation } from '@/app/hooks/useTranslation';

interface UrlFormWritingProps {
    onListenUrlsChange?: (url: string | null, isUrlWrong: boolean) => void;
    defaultUrl?: string;
}

const getBaseCardContent = (t: any) => `<ul style="color: #666666; margin-left: 8px; font-size: 12px; font-weight: 400; list-style-position: outside;">
 <li style="text-indent: 0; padding-left: 0px;">
    ${t('youtube_support_info', '현재 Youtube 영상만 지원합니다.')}
  </li>
  <li style="text-indent: 0; padding-left: 0px;">
    ${t('youtube_share_instruction', '유튜브에서 공유 버튼을 눌러 제공되는 URL을 입력해 주세요.')}
  </li>
  <li style="text-indent: 0; padding-left: 0px;">
    ${t('url_preview_instruction', '확인 버튼을 선택하여 노출되는 미리보기가 정상적인지 확인해주세요.')}
  </li>
</ul>`;

const UrlFormWriting = forwardRef<
    {
        handleCheckUrl: () => void;
    },
    UrlFormWritingProps
>(({ onListenUrlsChange, defaultUrl }, ref) => {
    const { t } = useTranslation();
    const [url, setUrl] = useState(defaultUrl ?? '');
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const [loadingThumbnail, setLoadingThumbnail] = useState(false);
    const [isWrongUrl, setIsWrongUrl] = useState<boolean>(false);
    const [isValidUrl, setIsValidUrl] = useState<boolean>(false);

    const handleChangeUrl = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const value = target.value;
        setUrl(value);

        // Realtime validation (but don't update parent URL yet)
        if (!value || value.trim() === '') {
            setIsWrongUrl(false);
            setIsValidUrl(false);
            setThumbnailUrl(null);
            // Don't notify parent when empty - only when there's an error
        } else {
            const isYouTubeUrlValid = utilsService.detectYouTubeUrl(value);
            setIsValidUrl(isYouTubeUrlValid);
            setIsWrongUrl(!isYouTubeUrlValid);

            if (!isYouTubeUrlValid) {
                setThumbnailUrl(null);
                // Only notify parent about invalid URL (for error display)
                onListenUrlsChange?.(value, true);
            } else {
                // Don't notify parent yet - wait for 확인 button
                // Just clear any previous error state
                onListenUrlsChange?.(null, false);
            }
        }
    };

    const handleCheckUrl = () => {
        if (!url || !isValidUrl) {
            setLoadingThumbnail(false);
            return;
        }

        setLoadingThumbnail(true);
        const checkUrlYoutube = utilsService.detectYouTubeUrl(url);

        if (checkUrlYoutube) {
            setThumbnailUrl(url);
            setIsWrongUrl(false);
            setLoadingThumbnail(false);
            onListenUrlsChange?.(url, false);
        } else {
            setThumbnailUrl(null);
            setIsWrongUrl(true);
            setLoadingThumbnail(false);
            onListenUrlsChange?.(url, true);
        }
    };

    useImperativeHandle(ref, () => ({
        handleCheckUrl,
    }));

    useEffect(() => {
        if (defaultUrl) {
            const isYouTubeUrlValid = utilsService.detectYouTubeUrl(defaultUrl);
            setIsValidUrl(isYouTubeUrlValid);
            setIsWrongUrl(!isYouTubeUrlValid);
            // If defaultUrl is valid, set thumbnailUrl to show player
            if (isYouTubeUrlValid) {
                setThumbnailUrl(defaultUrl);
            }
        } else {
            // Clear all state when defaultUrl is cleared (e.g., modal closed without attach)
            setUrl('');
            setThumbnailUrl(null);
            setIsValidUrl(false);
            setIsWrongUrl(false);
        }
    }, [defaultUrl]);

    return (
        <CenteredBox sx={{ flexDirection: 'column', paddingTop: 5, px: { mobile: 5, tablet: 12.5 } }}>
            <CenteredBox sx={{ flexDirection: 'column', justifyContent: 'start' }}>
                <FormControl
                    defaultValue=""
                    sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, border: 'black' }}
                >
                    <Typography
                        lineHeight="100%"
                        variant="h2_14Regular"
                        color="grayscale.gray07"
                    >
                        {t('url_label', 'URL')}
                    </Typography>
                    <BaseTextField
                        placeholder={t('url_placeholder', 'URL입력 (https://youtube.com)')}
                        value={url}
                        sx={{
                            '.MuiOutlinedInput-root': {
                                p: '16px 14px 18px 16px',
                                background: '#FFF',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: '1px solid #000',
                                },
                            },
                        }}
                        onChange={handleChangeUrl}
                        InputProps={{
                            sx: {
                                borderRadius: '16px',
                                fontSize: '12px',
                                fontWeight: 400,
                                lineHeight: '100%',
                            },
                            endAdornment: (
                                <Button
                                    onClick={handleCheckUrl}
                                    disabled={!url || !isValidUrl}
                                    sx={{ minWidth: '40px', mr: 0 }}
                                >
                                    <Typography
                                        variant="h2_12Medium"
                                        color={!url || !isValidUrl ? 'grayscale.gray04' : 'primary.main'}
                                        sx={{ width: '23px', textTransform: 'capitalize' }}
                                        lineHeight={'100%'}
                                    >
                                        {t('url_check_button', '확인')}
                                    </Typography>
                                </Button>
                            ),
                        }}
                        helperText={
                            Boolean(isWrongUrl) && (
                                <HelperText
                                    show={Boolean(isWrongUrl)}
                                    error={t('url_error_message', '링크주소가 올바르지 않습니다.')}
                                />
                            )
                        }
                    />
                </FormControl>
            </CenteredBox>
            <CenteredBox sx={{ flexDirection: 'column', alignItems: 'start', width: '100%', justifyContent: 'start', mt: 5, px: '10px' }}>
                <BaseCardNoticeGray
                    textAlign="start"
                    lineHeight="normal"
                    sx={{ alignItems: 'start', justifyContent: 'start', background: 'none', py: 0, width: '100%' }}
                    content={`${getBaseCardContent(t)}
                    `}
                />
            </CenteredBox>
            {loadingThumbnail && (
                <Skeleton
                    sx={{ mt: 10, width: '335px', height: '188px', background: '#F1F1F1', borderRadius: 2.5 }}
                    animation="wave"
                    variant="rectangular"
                />
            )}
            {thumbnailUrl && !loadingThumbnail && (
                <YouTubeThumbnail
                    url={thumbnailUrl}
                    onError={(hasError: boolean) => setIsWrongUrl(hasError)}
                />
            )}
        </CenteredBox>
    );
});

export default UrlFormWriting;
